/**
 * Segmented DSL reference for targeted inclusion in stage prompts.
 * Split from the monolithic pageGenerationSystemPrompt.ts for per-stage use.
 */

export const PAGE_DSL = `### \`<page>\`

Top-level page container. Must contain one or more \`<section>\` elements.

| Attribute          | Type     | Default         | Description                                  |
| ------------------ | -------- | --------------- | -------------------------------------------- |
| \`bg\`               | color    | \`background\`    | Background color (global style)              |
| \`bgImage\`          | string   | —               | Background image URL                         |
| \`bgImageOpacity\`   | integer  | \`100\`           | Background image opacity (0-100)             |
| \`bgImageScale\`     | integer  | \`100\`           | Background image scale (1-500)               |
| \`bgImageMode\`      | string   | \`fill\`          | Image mode: \`fill\`, \`fit\`, \`stretch\`, \`tile\` |
| \`bgImagePosition\`  | position | \`center center\` | Image position                               |
| \`bgVideoUrl\`       | string   | —               | Background video URL (YouTube or Vimeo)      |
| \`bgVideoOpacity\`   | integer  | \`100\`           | Background video opacity (0-100)             |
| \`bgVideoMode\`      | string   | \`fill\`          | Video mode: \`fill\`, \`fit\`                    |
| \`bgVideoEmbedCode\` | string   | —               | Video embed code (for embed source type)     |
| \`padding\`          | spacing  | \`20,20,20,20\`   | Padding                                      |
| \`maxWidth\`         | string   | \`1200px\`        | Maximum width (100% or pixel value)          |
| \`gap\`              | integer  | \`10\`            | Space between children                       |

#### Responsive Overrides

| Property   | Tablet            | Mobile            |
| ---------- | ----------------- | ----------------- |
| \`padding\`  | \`tablet-padding\`  | \`mobile-padding\`  |
| \`gap\`      | \`tablet-gap\`      | \`mobile-gap\`      |
| \`maxWidth\` | \`tablet-maxWidth\` | \`mobile-maxWidth\` |

#### Example

\`\`\`xml
<page bg="#f8f7f4" padding="20,20,20,20" gap="0" maxWidth="1200px">
  <section>...</section>
  <section>...</section>
</page>
\`\`\`

With background image:
\`\`\`xml
<page
  bg="background"
  bgImage="https://example.com/hero-bg.jpg"
  bgImageOpacity="30"
  bgImageMode="fill"
  bgImagePosition="center center"
  padding="0,0,0,0"
>
  <section>...</section>
</page>
\`\`\``;

export const CONTAINER_DSL = `## Container Elements

Containers can hold other containers and content elements.

### Common Container Attributes

| Attribute         | Type        | Description                                             |
| ----------------- | ----------- | ------------------------------------------------------- |
| \`direction\`       | string      | Flex direction: \`row\`, \`column\` (section only)          |
| \`width\`           | string      | Width override (e.g., \`30%\`, \`50%\`)                     |
| \`height\`          | string      | Height (default: \`auto\`)                                |
| \`maxWidth\`        | string      | Maximum width (e.g., \`1200px\`, \`80%\`)                   |
| \`bg\`              | color       | Background color                                        |
| \`bgImage\`         | string      | Background image URL                                    |
| \`bgImageOpacity\`  | integer     | Image opacity (0-100, default: 100)                     |
| \`bgImageScale\`    | integer     | Image scale (1-500, default: 100)                       |
| \`bgImageMode\`     | string      | Image mode: \`fill\`, \`fit\`, \`stretch\`, \`tile\`            |
| \`bgImagePosition\` | position    | Image position (default: \`center center\`)               |
| \`bgVideoUrl\`      | string      | Background video URL (YouTube or Vimeo)                 |
| \`bgVideoOpacity\`  | integer     | Video opacity (0-100, default: 100)                     |
| \`bgVideoMode\`     | string      | Video mode: \`fill\`, \`fit\` (default: \`fill\`)             |
| \`padding\`         | spacing     | Padding                                                 |
| \`margin\`          | spacing     | Margin                                                  |
| \`gap\`             | integer     | Gap between children                                    |
| \`align\`           | string      | Align items: \`start\`, \`center\`, \`end\`              |
| \`justify\`         | string      | Justify content: \`start\`, \`center\`, \`end\`, \`stretch\`        |
| \`borderRadius\`    | spacing     | Border radius                                           |
| \`borderWidth\`     | integer     | Border width                                            |
| \`borderColor\`     | color-solid | Border color                                            |
| \`opacity\`         | float       | Opacity (0-1)                                           |
| \`shadowType\`      | string      | Shadow type: \`none\`, \`drop\`                             |
| \`shadowX\`         | integer     | Shadow X offset in pixels (default: 0)                  |
| \`shadowY\`         | integer     | Shadow Y offset in pixels (default: 0)                  |
| \`shadowBlur\`      | integer     | Shadow blur radius in pixels (default: 0)               |
| \`shadowSpread\`    | integer     | Shadow spread in pixels (default: 0)                    |
| \`shadowColor\`     | string      | Shadow color as hex (default: \`#000000\`)                |
| \`position\`        | string      | CSS position: \`relative\`, \`absolute\`, \`fixed\`, \`sticky\` |
| \`top\`             | integer     | Top offset in pixels (e.g., \`10\` or \`10px\`)             |
| \`left\`            | integer     | Left offset in pixels                                   |
| \`right\`           | integer     | Right offset in pixels                                  |
| \`bottom\`          | integer     | Bottom offset in pixels                                 |
| \`zIndex\`          | integer     | Stacking order (-1 to 100)                              |

### \`<section>\`

| Attribute   | Default       | Description                       |
| ----------- | ------------- | --------------------------------- |
| \`direction\` | \`column\`      | Flex direction: \`row\` or \`column\` |
| \`padding\`   | \`40,40,40,40\` | Padding                           |
| \`align\`     | \`center\`      | Align items                       |
| \`justify\`   | \`center\`      | Justify content                   |
| \`gap\`       | \`10\`          | Gap between children              |

#### Shape Dividers (section only)

Decorative SVG shapes rendered at the top or bottom edge of a section. Only available on \`<section>\` elements.

| Attribute                | Type    | Default  | Description                                                      |
| ------------------------ | ------- | -------- | ---------------------------------------------------------------- |
| \`shapeDivider\`           | string  | —        | Shape type: \`wave\`, \`zigzag\`, \`curve\`, \`bowl\`, \`tilt\`         |
| \`shapeDividerPosition\`   | string  | \`bottom\` | Position: \`top\` or \`bottom\`                                     |
| \`shapeDividerHeight\`     | integer | \`80\`     | Height in pixels                                                 |
| \`shapeDividerFrequency\`  | integer | varies   | Repetitions of the pattern (wave/zigzag). Higher = more repeats. |
| \`shapeDividerFlip\`       | boolean | \`false\`  | Mirror the shape horizontally                                    |
| \`shapeDividerCurveCenter\`| integer | \`50\`     | Curve center position 0–100 (curve/bowl shapes only)             |

#### Section Example

\`\`\`xml
<section
  direction="row"
  padding="80,40,80,40"
  gap="40"
  bg="#1a1a2e"
  align="center"
  justify="center"
  mobile-direction="column"
  mobile-padding="40,20,40,20"
  mobile-gap="20"
>
  <column width="50%">...</column>
  <column width="50%">...</column>
</section>
\`\`\`

### \`<row>\`

| Attribute | Default       |
| --------- | ------------- |
| padding   | \`10,10,10,10\` |
| align     | \`flex-start\`  |
| justify   | \`center\`      |
| gap       | \`10\`          |

#### Row Example

\`\`\`xml
<row padding="0,0,0,0" gap="20" align="stretch" justify="space-between">
  <column width="33%">...</column>
  <column width="33%">...</column>
  <column width="33%">...</column>
</row>
\`\`\`

### \`<column>\`

| Attribute | Default       |
| --------- | ------------- |
| padding   | \`10,10,10,10\` |
| align     | \`center\`      |
| justify   | \`center\`      |
| gap       | \`10\`          |

#### Column Example

\`\`\`xml
<column
  width="60%"
  padding="20,20,20,20"
  gap="16"
  align="start"
  bg="rgba(220,220,220,0.2)"
  borderRadius="12,12,12,12"
  mobile-width="100%"
>
  <text variant="heading.h2">...</text>
  <text variant="body.large">...</text>
  <button variant="primary">Get Started</button>
</column>
\`\`\``;

export const RESPONSIVE_DSL = `## Responsive Breakpoint Attributes

Container elements support \`tablet-\` and \`mobile-\` prefixed attributes.

| Breakpoint | Screen Width   |
| ---------- | -------------- |
| Desktop    | >= 1200px      |
| Tablet     | 768px - 1199px |
| Mobile     | < 768px        |

### Overridable Properties

| Property       | Tablet                | Mobile                |
| -------------- | --------------------- | --------------------- |
| \`padding\`      | \`tablet-padding\`      | \`mobile-padding\`      |
| \`margin\`       | \`tablet-margin\`       | \`mobile-margin\`       |
| \`gap\`          | \`tablet-gap\`          | \`mobile-gap\`          |
| \`direction\`    | \`tablet-direction\`    | \`mobile-direction\`    |
| \`align\`        | \`tablet-align\`        | \`mobile-align\`        |
| \`justify\`      | \`tablet-justify\`      | \`mobile-justify\`      |
| \`width\`        | \`tablet-width\`        | \`mobile-width\`        |
| \`height\`       | \`tablet-height\`       | \`mobile-height\`       |
| \`maxWidth\`     | \`tablet-maxWidth\`     | \`mobile-maxWidth\`     |
| \`borderRadius\` | \`tablet-borderRadius\` | \`mobile-borderRadius\` |
| \`position\`     | \`tablet-position\`     | \`mobile-position\`     |
| \`top\`          | \`tablet-top\`          | \`mobile-top\`          |
| \`left\`         | \`tablet-left\`         | \`mobile-left\`         |
| \`right\`        | \`tablet-right\`        | \`mobile-right\`        |
| \`bottom\`       | \`tablet-bottom\`       | \`mobile-bottom\`       |
| \`zIndex\`       | \`tablet-zIndex\`       | \`mobile-zIndex\`       |

> **Note:** The base \`direction\` attribute (without prefix) is only on \`<section>\`. However, \`tablet-direction\` and \`mobile-direction\` work on all containers (\`<section>\`, \`<row>\`, \`<column>\`).

Example:
\`\`\`xml
<section
  direction="row"
  padding="60,40,60,40"
  gap="40"
  mobile-direction="column"
  mobile-padding="40,20,40,20"
  mobile-gap="20"
>
  <column padding="20,20,20,20" mobile-padding="10,10,10,10">...</column>
  <column>...</column>
</section>
\`\`\``;

export const TEXT_DSL = `### \`<text>\`

Rich text content. Must contain \`<paragraph>\` tags.

| Attribute      | Type    | Default      | Description                                             |
| -------------- | ------- | ------------ | ------------------------------------------------------- |
| \`variant\`      | string  | \`heading.h2\` | Global style variant                                    |
| \`align\`        | string  | \`left\`       | Text alignment: \`left\`, \`center\`, \`right\`, \`justify\`    |
| \`color\`        | color   | —            | Text color override                                     |
| \`padding\`      | spacing | \`0,0,0,0\`    | Padding                                                 |
| \`margin\`       | spacing | \`0,0,0,0\`    | Margin                                                  |
| \`borderRadius\` | spacing | \`0,0,0,0\`    | Border radius                                           |
| \`maxWidth\`     | integer | \`1200\`       | Maximum width                                           |
| \`width\`        | string  | \`100%\`       | Width                                                   |
| \`attached\`     | boolean | \`true\`       | Use global text styles                                  |
| \`position\`     | string  | \`relative\`   | CSS position: \`relative\`, \`absolute\`, \`fixed\`, \`sticky\` |
| \`top\`          | integer | —            | Top offset in pixels                                    |
| \`left\`         | integer | —            | Left offset in pixels                                   |
| \`right\`        | integer | —            | Right offset in pixels                                  |
| \`bottom\`       | integer | —            | Bottom offset in pixels                                 |
| \`zIndex\`       | integer | \`0\`          | Stacking order                                          |

When \`attached="false"\`, the text container is detached from global styles:

| Attribute         | Type   | Default | Description          |
| ----------------- | ------ | ------- | -------------------- |
| \`backgroundColor\` | color  | —       | Container background |
| \`lineHeight\`      | number | \`1.5\`   | Line height          |

#### Responsive Overrides

| Property       | Tablet                | Mobile                |
| -------------- | --------------------- | --------------------- |
| \`width\`        | \`tablet-width\`        | \`mobile-width\`        |
| \`padding\`      | \`tablet-padding\`      | \`mobile-padding\`      |
| \`margin\`       | \`tablet-margin\`       | \`mobile-margin\`       |
| \`borderRadius\` | \`tablet-borderRadius\` | \`mobile-borderRadius\` |
| \`maxWidth\`     | \`tablet-maxWidth\`     | \`mobile-maxWidth\`     |
| \`position\`     | \`tablet-position\`     | \`mobile-position\`     |
| \`top\`          | \`tablet-top\`          | \`mobile-top\`          |
| \`left\`         | \`tablet-left\`         | \`mobile-left\`         |
| \`right\`        | \`tablet-right\`        | \`mobile-right\`        |
| \`bottom\`       | \`tablet-bottom\`       | \`mobile-bottom\`       |
| \`zIndex\`       | \`tablet-zIndex\`       | \`mobile-zIndex\`       |

#### Text Variants

| Variant       | Description               |
| ------------- | ------------------------- |
| \`heading.h1\`  | Heading level 1           |
| \`heading.h2\`  | Heading level 2 (default) |
| \`heading.h3\`  | Heading level 3           |
| \`heading.h4\`  | Heading level 4           |
| \`heading.h5\`  | Heading level 5           |
| \`heading.h6\`  | Heading level 6           |
| \`body.small\`  | Small body text           |
| \`body.medium\` | Medium body text          |
| \`body.large\`  | Large body text           |

#### \`<paragraph>\` Tag

Paragraph attributes apply styling to all text within the paragraph.

**Always Applied (attached or detached):**

| Attribute        | Type   | Default | Description                         |
| ---------------- | ------ | ------- | ----------------------------------- |
| \`color\`          | color  | —       | Text color                          |
| \`textDecoration\` | string | —       | Text decoration (e.g., \`underline\`) |

**Only When Detached (\`attached="false"\`):**

| Attribute       | Type   | Default | Description                        |
| --------------- | ------ | ------- | ---------------------------------- |
| \`fontSize\`      | string | —       | Font size (e.g., \`24px\`)           |
| \`fontWeight\`    | string | —       | Font weight (e.g., \`700\`, \`bold\`)  |
| \`fontFamily\`    | string | —       | Font family (e.g., \`Georgia\`)      |
| \`letterSpacing\` | number | —       | Letter spacing in pixels           |
| \`textTransform\` | string | —       | Text transform (e.g., \`uppercase\`) |
| \`fontStyle\`     | string | —       | Font style (e.g., \`italic\`)        |

#### \`<link>\` Tag (inside paragraph)

Links inherit paragraph styles but can override them with their own attributes.

| Attribute        | Type   | Default | Description                      |
| ---------------- | ------ | ------- | -------------------------------- |
| \`href\`           | string | —       | Link URL                         |
| \`target\`         | string | —       | Link target (\`_blank\`)           |
| \`variant\`        | string | \`link1\` | Link style variant (\`link1\`, \`link2\`) |
| \`color\`          | color  | —       | Text color (overrides paragraph) |
| \`textDecoration\` | string | —       | Text decoration                  |

When detached, links also support: \`fontSize\`, \`fontWeight\`, \`fontFamily\`, \`letterSpacing\`, \`textTransform\`, \`fontStyle\`.

#### Text Examples

Attached text with heading:
\`\`\`xml
<text variant="heading.h1" align="center">
  <paragraph>Build Better Landing Pages</paragraph>
</text>
\`\`\`

Attached text with styled paragraph and link:
\`\`\`xml
<text variant="body.medium" align="center">
  <paragraph color="text">
    Questions? <link href="/contact" variant="link1">Contact our team</link> for help.
  </paragraph>
</text>
\`\`\`

Detached text with custom styling:
\`\`\`xml
<text attached="false" align="center" maxWidth="600">
  <paragraph fontSize="18px" fontWeight="600" fontFamily="Poppins" color="primary">
    Join 10,000+ customers who trust us
  </paragraph>
</text>
\`\`\``;

export const BUTTON_DSL = `### \`<button>\`

Button with text content between tags.

| Attribute      | Type    | Default    | Description                                             |
| -------------- | ------- | ---------- | ------------------------------------------------------- |
| \`variant\`      | string  | \`primary\`  | Style variant: \`primary\`, \`secondary\`, \`accent\`         |
| \`size\`         | string  | \`medium\`   | Size: \`small\`, \`medium\`, \`large\`                        |
| \`fullWidth\`    | boolean | \`false\`    | Fill container width                                    |
| \`clickUrl\`     | string  | —          | URL to navigate on click                                |
| \`openInNewTab\` | boolean | \`false\`    | Open link in new tab                                    |
| \`attached\`     | boolean | \`true\`     | Use global button styles                                |
| \`shadowType\`   | string  | \`none\`     | Shadow type: \`none\`, \`drop\`                             |
| \`shadowX\`      | integer | \`0\`        | Shadow X offset in pixels                               |
| \`shadowY\`      | integer | \`0\`        | Shadow Y offset in pixels                               |
| \`shadowBlur\`   | integer | \`0\`        | Shadow blur radius in pixels                            |
| \`shadowSpread\` | integer | \`0\`        | Shadow spread in pixels                                 |
| \`shadowColor\`  | string  | \`#000000\`  | Shadow color as hex                                     |
| \`position\`     | string  | \`relative\` | CSS position: \`relative\`, \`absolute\`, \`fixed\`, \`sticky\` |
| \`top\`          | integer | —          | Top offset in pixels                                    |
| \`left\`         | integer | —          | Left offset in pixels                                   |
| \`right\`        | integer | —          | Right offset in pixels                                  |
| \`bottom\`       | integer | —          | Bottom offset in pixels                                 |
| \`zIndex\`       | integer | \`0\`        | Stacking order                                          |

When \`attached="false"\`, the button is detached from global styles:

| Attribute          | Type    | Default     | Description           |
| ------------------ | ------- | ----------- | --------------------- |
| \`fontSize\`         | string  | \`16px\`      | Text size             |
| \`fontWeight\`       | string  | \`400\`       | Font weight           |
| \`fontFamily\`       | string  | \`Inter\`     | Font family           |
| \`fontStyle\`        | string  | \`normal\`    | Font style            |
| \`color\`            | color   | white       | Text color            |
| \`bg\`               | color   | black       | Background color      |
| \`padding\`          | spacing | \`6,16,6,16\` | Padding               |
| \`margin\`           | spacing | \`0,0,0,0\`   | Margin                |
| \`borderRadius\`     | spacing | \`0,0,0,0\`   | Border radius         |
| \`borderWidth\`      | integer | \`0\`         | Border width          |
| \`borderColor\`      | color   | —           | Border color          |
| \`hoverBg\`          | color   | —           | Background on hover   |
| \`hoverBorderColor\` | color   | —           | Border color on hover |
| \`hoverTextColor\`   | color   | —           | Text color on hover   |
| \`letterSpacing\`    | number  | \`0\`         | Letter spacing (em)   |
| \`textAlign\`        | string  | \`center\`    | Text alignment        |

#### Responsive Overrides

| Property       | Tablet                | Mobile                |
| -------------- | --------------------- | --------------------- |
| \`padding\`      | \`tablet-padding\`      | \`mobile-padding\`      |
| \`margin\`       | \`tablet-margin\`       | \`mobile-margin\`       |
| \`borderRadius\` | \`tablet-borderRadius\` | \`mobile-borderRadius\` |
| \`position\`     | \`tablet-position\`     | \`mobile-position\`     |
| \`top\`          | \`tablet-top\`          | \`mobile-top\`          |
| \`left\`         | \`tablet-left\`         | \`mobile-left\`         |
| \`right\`        | \`tablet-right\`        | \`mobile-right\`        |
| \`bottom\`       | \`tablet-bottom\`       | \`mobile-bottom\`       |
| \`zIndex\`       | \`tablet-zIndex\`       | \`mobile-zIndex\`       |

#### Button Variants

| Variant     | Description                    |
| ----------- | ------------------------------ |
| \`primary\`   | Primary button style (default) |
| \`secondary\` | Secondary button style         |
| \`accent\`    | Accent button style            |

#### Button Examples

Primary CTA button:
\`\`\`xml
<button variant="primary" size="large" clickUrl="/signup" openInNewTab="false">
  Start Your Free Trial
</button>
\`\`\`

Detached button with custom styling:
\`\`\`xml
<button
  attached="false"
  bg="#4f46e5"
  color="#ffffff"
  hoverBg="#4338ca"
  fontSize="16px"
  fontWeight="600"
  padding="12,24,12,24"
  borderRadius="8,8,8,8"
  clickUrl="/pricing"
>
  View Pricing
</button>
\`\`\``;

export const IMAGE_DSL = `### \`<image>\`

| Attribute          | Type        | Default      | Description                                                   |
| ------------------ | ----------- | ------------ | ------------------------------------------------------------- |
| \`src\`              | string      | —            | Image URL (required)                                          |
| \`alt\`              | string      | \`""\`         | Alt text                                                      |
| \`fit\`              | string      | \`fill\`       | Fit: \`contain\`, \`cover\`, \`fill\`, \`tile\`, \`none\`, \`scale-down\` |
| \`width\`            | string      | \`100%\`       | Width                                                         |
| \`height\`           | string      | \`512px\`      | Height                                                        |
| \`maxWidth\`         | string      | \`1200px\`     | Maximum width in pixels (use default for 100%)                |
| \`objectPosition\`   | string      | \`center top\` | Image position within container                               |
| \`padding\`          | spacing     | \`0,0,0,0\`    | Padding                                                       |
| \`margin\`           | spacing     | \`0,0,0,0\`    | Margin                                                        |
| \`borderRadius\`     | spacing     | \`0,0,0,0\`    | Border radius                                                 |
| \`borderWidth\`      | integer     | \`0\`          | Border width                                                  |
| \`borderColor\`      | color-solid | —            | Border color                                                  |
| \`hoverBorderColor\` | color-solid | —            | Border color on hover                                         |
| \`clickUrl\`         | string      | —            | URL on click                                                  |
| \`openInNewTab\`     | boolean     | \`false\`      | Open link in new tab                                          |
| \`opacity\`          | float       | \`1\`          | Opacity (0-1)                                                 |
| \`bg\`               | color       | —            | Background color                                              |
| \`shadowType\`       | string      | \`none\`       | Shadow type: \`none\`, \`drop\`                                   |
| \`shadowX\`          | integer     | \`0\`          | Shadow X offset in pixels                                     |
| \`shadowY\`          | integer     | \`0\`          | Shadow Y offset in pixels                                     |
| \`shadowBlur\`       | integer     | \`0\`          | Shadow blur radius in pixels                                  |
| \`shadowSpread\`     | integer     | \`0\`          | Shadow spread in pixels                                       |
| \`shadowColor\`      | string      | \`#000000\`    | Shadow color as hex                                           |
| \`position\`         | string      | \`relative\`   | CSS position: \`relative\`, \`absolute\`, \`fixed\`, \`sticky\`       |
| \`top\`              | integer     | —            | Top offset in pixels                                          |
| \`left\`             | integer     | —            | Left offset in pixels                                         |
| \`right\`            | integer     | —            | Right offset in pixels                                        |
| \`bottom\`           | integer     | —            | Bottom offset in pixels                                       |
| \`zIndex\`           | integer     | \`0\`          | Stacking order                                                |

#### Responsive Overrides

| Property       | Tablet                | Mobile                |
| -------------- | --------------------- | --------------------- |
| \`width\`        | \`tablet-width\`        | \`mobile-width\`        |
| \`height\`       | \`tablet-height\`       | \`mobile-height\`       |
| \`maxWidth\`     | \`tablet-maxWidth\`     | \`mobile-maxWidth\`     |
| \`padding\`      | \`tablet-padding\`      | \`mobile-padding\`      |
| \`margin\`       | \`tablet-margin\`       | \`mobile-margin\`       |
| \`borderRadius\` | \`tablet-borderRadius\` | \`mobile-borderRadius\` |

#### Image Examples

Hero image with cover fit:
\`\`\`xml
<image
  src="https://example.com/hero.jpg"
  alt="Team collaboration"
  fit="cover"
  width="100%"
  height="400px"
  objectPosition="center center"
  mobile-height="250px"
/>
\`\`\`

Product image with contain fit (no cropping):
\`\`\`xml
<image
  src="https://example.com/product.png"
  alt="Product screenshot"
  fit="contain"
  width="100%"
  height="300px"
  bg="rgba(220,220,220,0.2)"
  borderRadius="8,8,8,8"
/>
\`\`\`

Rounded avatar image:
\`\`\`xml
<image
  src="https://example.com/avatar.jpg"
  alt="Sarah Johnson"
  fit="cover"
  width="80px"
  height="80px"
  borderRadius="40,40,40,40"
/>
\`\`\``;

export const VIDEO_DSL = `### \`<video>\`

| Attribute      | Type        | Default    | Description                                             |
| -------------- | ----------- | ---------- | ------------------------------------------------------- |
| \`url\`          | string      | —          | Video URL (required)                                    |
| \`provider\`     | string      | \`youtube\`  | Provider: \`youtube\`, \`vimeo\`, \`wistia\`                  |
| \`autoplay\`     | boolean     | \`false\`    | Auto-play video                                         |
| \`controls\`     | boolean     | \`true\`     | Show controls                                           |
| \`width\`        | string      | \`100%\`     | Width                                                   |
| \`height\`       | string      | \`315px\`    | Height                                                  |
| \`padding\`      | spacing     | \`0,0,0,0\`  | Padding                                                 |
| \`margin\`       | spacing     | \`0,0,0,0\`  | Margin                                                  |
| \`borderRadius\` | spacing     | \`0,0,0,0\`  | Border radius                                           |
| \`borderWidth\`  | integer     | \`0\`        | Border width                                            |
| \`borderColor\`  | color-solid | —          | Border color                                            |
| \`opacity\`      | float       | \`1\`        | Opacity (0-1)                                           |
| \`bg\`           | color       | —          | Background color                                        |
| \`shadowType\`   | string      | \`none\`     | Shadow type: \`none\`, \`drop\`                             |
| \`shadowX\`      | integer     | \`0\`        | Shadow X offset in pixels                               |
| \`shadowY\`      | integer     | \`0\`        | Shadow Y offset in pixels                               |
| \`shadowBlur\`   | integer     | \`0\`        | Shadow blur radius in pixels                            |
| \`shadowSpread\` | integer     | \`0\`        | Shadow spread in pixels                                 |
| \`shadowColor\`  | string      | \`#000000\`  | Shadow color as hex                                     |
| \`position\`     | string      | \`relative\` | CSS position: \`relative\`, \`absolute\`, \`fixed\`, \`sticky\` |
| \`top\`          | integer     | —          | Top offset in pixels                                    |
| \`left\`         | integer     | —          | Left offset in pixels                                   |
| \`right\`        | integer     | —          | Right offset in pixels                                  |
| \`bottom\`       | integer     | —          | Bottom offset in pixels                                 |
| \`zIndex\`       | integer     | \`0\`        | Stacking order                                          |

#### Responsive Overrides

| Property       | Tablet                | Mobile                |
| -------------- | --------------------- | --------------------- |
| \`width\`        | \`tablet-width\`        | \`mobile-width\`        |
| \`height\`       | \`tablet-height\`       | \`mobile-height\`       |
| \`padding\`      | \`tablet-padding\`      | \`mobile-padding\`      |
| \`margin\`       | \`tablet-margin\`       | \`mobile-margin\`       |
| \`borderRadius\` | \`tablet-borderRadius\` | \`mobile-borderRadius\` |

#### Video Examples

YouTube video embed:
\`\`\`xml
<video
  url="https://www.youtube.com/watch?v=dQw4w9WgXcQ"
  provider="youtube"
  width="100%"
  height="400px"
  controls="true"
  autoplay="false"
  borderRadius="12,12,12,12"
  mobile-height="250px"
/>
\`\`\``;

export const COUNTDOWN_DSL = `### \`<countdown>\`

Displays a countdown timer with configurable target date, mode, and styling.

| Attribute             | Type    | Default        | Description                                             |
| --------------------- | ------- | -------------- | ------------------------------------------------------- |
| \`mode\`                | string  | \`standard\`     | Countdown mode: \`standard\`, \`daily\`, \`evergreen\`        |
| \`dateTime\`            | string  | 24h from now   | ISO date string for target date/time                    |
| \`evergreenId\`         | string  | auto-generated | Unique ID for evergreen mode tracking                   |
| \`evergreenDuration\`   | integer | \`604800\`       | Duration in seconds for evergreen mode (1 week)         |
| \`type\`                | string  | \`default\`      | Display format: \`default\`, \`short\`                      |
| \`showDays\`            | boolean | \`true\`         | Show days unit                                          |
| \`showHours\`           | boolean | \`true\`         | Show hours unit                                         |
| \`showMinutes\`         | boolean | \`true\`         | Show minutes unit                                       |
| \`showSeconds\`         | boolean | \`true\`         | Show seconds unit                                       |
| \`labelDays\`           | string  | \`Days\`         | Label for days                                          |
| \`labelHours\`          | string  | \`Hours\`        | Label for hours                                         |
| \`labelMinutes\`        | string  | \`Minutes\`      | Label for minutes                                       |
| \`labelSeconds\`        | string  | \`Seconds\`      | Label for seconds                                       |
| \`width\`               | string  | \`100%\`         | Width                                                   |
| \`height\`              | string  | \`auto\`         | Height                                                  |
| \`padding\`             | spacing | \`16,24,16,24\`  | Padding                                                 |
| \`margin\`              | spacing | \`0,0,0,0\`      | Margin                                                  |
| \`gap\`                 | integer | \`16\`           | Gap between countdown tiles                             |
| \`borderRadius\`        | spacing | \`8,8,8,8\`      | Border radius                                           |
| \`borderWidth\`         | integer | \`0\`            | Border width                                            |
| \`borderColor\`         | color   | —              | Border color                                            |
| \`bg\`                  | color   | —              | Background color                                        |
| \`numberFontFamily\`    | string  | \`Inter\`        | Font family for numbers                                 |
| \`numberFontWeight\`    | string  | \`700\`          | Font weight for numbers                                 |
| \`numberFontStyle\`     | string  | \`normal\`       | Font style for numbers                                  |
| \`numberFontSize\`      | string  | \`32px\`         | Font size for numbers                                   |
| \`numberLetterSpacing\` | number  | \`0\`            | Letter spacing for numbers (em)                         |
| \`numberColor\`         | color   | white          | Number text color                                       |
| \`labelFontFamily\`     | string  | \`Inter\`        | Font family for labels                                  |
| \`labelFontWeight\`     | string  | \`400\`          | Font weight for labels                                  |
| \`labelFontStyle\`      | string  | \`normal\`       | Font style for labels                                   |
| \`labelFontSize\`       | string  | \`12px\`         | Font size for labels                                    |
| \`labelLetterSpacing\`  | number  | \`0.05\`         | Letter spacing for labels (em)                          |
| \`labelColor\`          | color   | gray           | Label text color                                        |
| \`clickUrl\`            | string  | —              | URL to navigate on click                                |
| \`openInNewTab\`        | boolean | \`false\`        | Open click link in new tab                              |
| \`expireUrl\`           | string  | —              | URL to redirect when countdown expires                  |
| \`expireInNewTab\`      | boolean | \`false\`        | Open expire link in new tab                             |
| \`shadowType\`          | string  | \`none\`         | Shadow type: \`none\`, \`drop\`                             |
| \`shadowX\`             | integer | \`0\`            | Shadow X offset in pixels                               |
| \`shadowY\`             | integer | \`0\`            | Shadow Y offset in pixels                               |
| \`shadowBlur\`          | integer | \`0\`            | Shadow blur radius in pixels                            |
| \`shadowSpread\`        | integer | \`0\`            | Shadow spread in pixels                                 |
| \`shadowColor\`         | string  | \`#000000\`      | Shadow color as hex                                     |
| \`position\`            | string  | \`relative\`     | CSS position: \`relative\`, \`absolute\`, \`fixed\`, \`sticky\` |
| \`top\`                 | integer | —              | Top offset in pixels                                    |
| \`left\`                | integer | —              | Left offset in pixels                                   |
| \`right\`               | integer | —              | Right offset in pixels                                  |
| \`bottom\`              | integer | —              | Bottom offset in pixels                                 |
| \`zIndex\`              | integer | \`0\`            | Stacking order                                          |

#### Responsive Overrides

| Property       | Tablet                | Mobile                |
| -------------- | --------------------- | --------------------- |
| \`padding\`      | \`tablet-padding\`      | \`mobile-padding\`      |
| \`margin\`       | \`tablet-margin\`       | \`mobile-margin\`       |
| \`borderRadius\` | \`tablet-borderRadius\` | \`mobile-borderRadius\` |
| \`position\`     | \`tablet-position\`     | \`mobile-position\`     |
| \`top\`          | \`tablet-top\`          | \`mobile-top\`          |
| \`left\`         | \`tablet-left\`         | \`mobile-left\`         |
| \`right\`        | \`tablet-right\`        | \`mobile-right\`        |
| \`bottom\`       | \`tablet-bottom\`       | \`mobile-bottom\`       |
| \`zIndex\`       | \`tablet-zIndex\`       | \`mobile-zIndex\`       |

#### Countdown Modes

| Mode        | Description                                                                 |
| ----------- | --------------------------------------------------------------------------- |
| \`standard\`  | Counts down to a specific date/time set via \`dateTime\`                      |
| \`daily\`     | Resets daily at the time specified in \`dateTime\`                            |
| \`evergreen\` | Per-visitor countdown using \`evergreenDuration\`; persists via \`evergreenId\` |

#### Example

\`\`\`xml
<countdown
  mode="standard"
  dateTime="2025-12-31T23:59:59"
  showDays="true"
  showHours="true"
  numberFontSize="48px"
  numberColor="#ffffff"
  labelFontSize="14px"
  labelColor="#9ca3af"
  bg="primary"
  padding="24,32,24,32"
  borderRadius="12,12,12,12"
  gap="20"
/>
\`\`\``;

export const FORM_DSL = `### \`<form>\`

Form container. Can only contain form field elements.

#### Container Attributes

| Attribute          | Type        | Default        | Description           |
| ------------------ | ----------- | -------------- | --------------------- |
| \`name\`             | string      | \`Contact Form\` | Form name             |
| \`bg\`               | color       | —              | Background color      |
| \`padding\`          | spacing     | \`16,16,16,16\`  | Padding               |
| \`margin\`           | spacing     | \`0,0,0,0\`      | Margin                |
| \`borderRadius\`     | spacing     | \`8,8,8,8\`      | Border radius         |
| \`borderWidth\`      | integer     | \`1\`            | Border width          |
| \`borderColor\`      | color-solid | \`accent\`       | Border color          |
| \`hoverBorderColor\` | color-solid | —              | Border color on hover |
| \`shadowType\`       | string      | \`none\`         | Shadow type: \`none\`, \`drop\` |
| \`shadowX\`          | integer     | \`0\`            | Shadow X offset in pixels   |
| \`shadowY\`          | integer     | \`0\`            | Shadow Y offset in pixels   |
| \`shadowBlur\`       | integer     | \`0\`            | Shadow blur radius in pixels |
| \`shadowSpread\`     | integer     | \`0\`            | Shadow spread in pixels     |
| \`shadowColor\`      | string      | \`#000000\`      | Shadow color as hex         |

#### Field Default Attributes

These attributes set defaults for all fields within the form:

| Attribute                   | Type        | Default      | Description                  |
| --------------------------- | ----------- | ------------ | ---------------------------- |
| \`fieldMargin\`               | spacing     | \`0,0,16,0\`   | Margin for all fields        |
| \`fieldBorderRadius\`         | spacing     | \`6,6,6,6\`    | Border radius for all fields |
| \`fieldInputPadding\`         | spacing     | \`6,8,6,8\`    | Input padding for all fields |
| \`fieldLabelFontFamily\`      | string      | \`Inter\`      | Label font family            |
| \`fieldLabelFontSize\`        | string      | \`10px\`       | Label font size              |
| \`fieldLabelFontWeight\`      | integer     | \`500\`        | Label font weight            |
| \`fieldLabelColor\`           | color-solid | \`text\`       | Label color                  |
| \`fieldInputFontFamily\`           | string      | \`Inter\`      | Input font family            |
| \`fieldInputFontSize\`             | string      | \`12px\`       | Input font size              |
| \`fieldInputFontWeight\`           | integer     | \`500\`        | Input font weight            |
| \`fieldInputTextColor\`            | color-solid | \`text\`       | Input text color             |
| \`fieldInputBackgroundColor\`      | color-solid | \`background\` | Input background             |
| \`fieldInputBorderColor\`          | color-solid | \`accent\`     | Input border color           |
| \`fieldInputBorderWidth\`          | integer     | \`1\`          | Input border width           |
| \`fieldInputHoverBackgroundColor\` | color-solid | —            | Input hover background       |
| \`fieldInputHoverBorderColor\`     | color-solid | —            | Input hover border color     |
| \`fieldHelperTextColor\`           | color-solid | \`text\`       | Helper text color            |
| \`fieldCheckboxCheckedColor\`      | color-solid | \`primary\`    | Checkbox checked color       |

#### Responsive Overrides

| Property       | Tablet                | Mobile                |
| -------------- | --------------------- | --------------------- |
| \`padding\`      | \`tablet-padding\`      | \`mobile-padding\`      |
| \`margin\`       | \`tablet-margin\`       | \`mobile-margin\`       |
| \`borderRadius\` | \`tablet-borderRadius\` | \`mobile-borderRadius\` |

#### Field Elements

| Element         | Description                      | Key Attributes                                  |
| --------------- | -------------------------------- | ----------------------------------------------- |
| \`<shortText>\`   | Single-line text input           | \`label\`, \`placeholder\`, \`required\`, \`showLabel\`  |
| \`<email>\`       | Email input field                | \`label\`, \`placeholder\`, \`required\`, \`showLabel\`  |
| \`<phone>\`       | Phone number input               | \`label\`, \`placeholder\`, \`required\`, \`showLabel\`  |
| \`<dropdown>\`    | Dropdown select (with \`<option>\`) | \`label\`, \`placeholder\`, \`required\`, \`singleSelect\` |
| \`<select>\`      | Radio/checkbox selection         | \`label\`, \`required\`, \`singleSelect\`              |
| \`<consent>\`     | Checkbox consent field           | \`text\`, \`required\`, \`description\`                |
| \`<submitButton>\` | Form submission button           | \`text\`, \`variant\`, \`size\`, \`fullWidth\`           |

#### Form Example:

\`\`\`xml
<form name="contact" padding="24,24,24,24" borderRadius="12,12,12,12">
  <shortText label="Full Name" showLabel="true" placeholder="Your full name" required="true" />
  <email label="Email" showLabel="true" placeholder="you@example.com" required="true" />
  <submitButton text="Send Message" variant="primary" />
</form>
\`\`\``;

export const ICON_DSL = `### \`<icon>\`

Displays a FontAwesome icon.

| Attribute         | Type        | Default    | Description                                             |
| ----------------- | ----------- | ---------- | ------------------------------------------------------- |
| \`iconName\`        | string      | \`star\`     | FontAwesome icon name (without \`fa-\` prefix)            |
| \`iconStyle\`       | string      | \`regular\`  | Icon style: \`solid\`, \`regular\`                         |
| \`color\`           | color-solid | black      | Icon color                                              |
| \`opacity\`         | float       | \`1\`        | Opacity (0-1)                                           |
| \`width\`           | string      | \`48px\`     | Width                                                   |
| \`height\`          | string      | \`48px\`     | Height                                                  |
| \`padding\`         | spacing     | \`0,0,0,0\`  | Padding                                                 |
| \`margin\`          | spacing     | \`0,0,0,0\`  | Margin                                                  |
| \`borderRadius\`    | spacing     | \`0,0,0,0\`  | Border radius                                           |
| \`borderWidth\`     | integer     | \`0\`        | Border width                                            |
| \`borderColor\`     | color-solid | —          | Border color                                            |
| \`backgroundColor\` | color       | —          | Background color                                        |
| \`shadowType\`      | string      | \`none\`     | Shadow type: \`none\`, \`drop\`                             |
| \`shadowX\`         | integer     | \`0\`        | Shadow X offset in pixels                               |
| \`shadowY\`         | integer     | \`0\`        | Shadow Y offset in pixels                               |
| \`shadowBlur\`      | integer     | \`0\`        | Shadow blur radius in pixels                            |
| \`shadowSpread\`    | integer     | \`0\`        | Shadow spread in pixels                                 |
| \`shadowColor\`     | string      | \`#000000\`  | Shadow color as hex                                     |
| \`position\`        | string      | \`relative\` | CSS position: \`relative\`, \`absolute\`, \`fixed\`, \`sticky\` |
| \`top\`             | integer     | —          | Top offset in pixels                                    |
| \`left\`            | integer     | —          | Left offset in pixels                                   |
| \`right\`           | integer     | —          | Right offset in pixels                                  |
| \`bottom\`          | integer     | —          | Bottom offset in pixels                                 |
| \`zIndex\`          | integer     | \`0\`        | Stacking order                                          |

#### Responsive Overrides

| Property       | Tablet                | Mobile                |
| -------------- | --------------------- | --------------------- |
| \`width\`        | \`tablet-width\`        | \`mobile-width\`        |
| \`height\`       | \`tablet-height\`       | \`mobile-height\`       |
| \`padding\`      | \`tablet-padding\`      | \`mobile-padding\`      |
| \`margin\`       | \`tablet-margin\`       | \`mobile-margin\`       |
| \`borderRadius\` | \`tablet-borderRadius\` | \`mobile-borderRadius\` |

#### Common Icon Names

| Category    | Icons                                                                     |
| ----------- | ------------------------------------------------------------------------- |
| Actions     | \`check\`, \`xmark\`, \`plus\`, \`minus\`, \`pen\`, \`trash\`, \`download\`, \`upload\` |
| Arrows      | \`arrow-right\`, \`arrow-left\`, \`arrow-up\`, \`arrow-down\`, \`chevron-right\`  |
| Social      | \`heart\`, \`thumbs-up\`, \`share\`, \`comment\`, \`bell\`, \`bookmark\`              |
| Objects     | \`star\`, \`gift\`, \`trophy\`, \`crown\`, \`gem\`, \`lightbulb\`, \`bolt\`, \`fire\`  |
| Business    | \`briefcase\`, \`chart-line\`, \`handshake\`, \`building\`, \`globe\`               |
| People      | \`user\`, \`users\`, \`circle-user\`, \`people-group\`                            |
| Media       | \`image\`, \`camera\`, \`play\`, \`music\`, \`video\`                              |
| Technology  | \`laptop\`, \`mobile\`, \`envelope\`, \`phone\`, \`wifi\`, \`code\`                 |
| Navigation  | \`bars\`, \`magnifying-glass\`, \`house\`, \`gear\`, \`circle-info\`               |
| Security    | \`lock\`, \`shield\`, \`key\`, \`eye\`, \`eye-slash\`                              |
| Commerce    | \`cart-shopping\`, \`credit-card\`, \`tag\`, \`receipt\`, \`money-bill\`            |
| Time/Date   | \`clock\`, \`calendar\`, \`hourglass\`, \`stopwatch\`                             |

#### Icon Examples

Simple star icon:
\`\`\`xml
<icon iconName="star" iconStyle="solid" color="primary" width="32px" height="32px" />
\`\`\`

Feature icon with background:
\`\`\`xml
<icon
  iconName="lightbulb"
  iconStyle="solid"
  color="#ffffff"
  width="48px"
  height="48px"
  backgroundColor="primary"
  padding="12,12,12,12"
  borderRadius="24,24,24,24"
/>
\`\`\`

Checkmark list icon:
\`\`\`xml
<icon iconName="check" iconStyle="solid" color="#22c55e" width="24px" height="24px" />
\`\`\``;

export const COLOR_DSL = `## Color Types

| Type          | Supported Formats                              |
| ------------- | ---------------------------------------------- |
| \`color\`       | Named refs, hex, rgb, rgba, **gradient**       |
| \`color-solid\` | Named refs, hex, rgb, rgba (no gradients)      |

### Named Colors (Global Style References)
\`primary\`, \`secondary\`, \`tertiary\`, \`text\`, \`accent\`, \`background\`

### Explicit Color Formats
- Hex: \`#fff\`, \`#ffffff\`, \`#ffffffff\`
- RGB: \`rgb(255, 255, 255)\`
- RGBA: \`rgba(255, 255, 255, 0.5)\`

### Gradient Syntax (for \`color\` type only)
\`gradient(type, angle, color:position, color:position, ...)\`
- Types: \`linear\`, \`radial\`
- Example: \`gradient(linear, 90, #ff0000:0, #0000ff:100)\`
- Supported on: text \`color\`, container/page/image \`bg\`
`;

export const SPACING_DSL = `## Spacing Values

- Single value: \`"10"\` or \`"10px"\` applies to all sides
- Four values: \`"10,20,10,20"\` for top, right, bottom, left
`;

export const FONT_FAMILY_DSL = `## Font Families

Available font families for \`fontFamily\` attributes on text, paragraphs, links, buttons, countdown, and form elements:

| Font Family           | Available Weights                                                             |
| --------------------- | ----------------------------------------------------------------------------- |
| \`Anton\`               | regular                                                                       |
| \`Archivo\`             | thin, extra-light, light, regular, medium, semi-bold, bold, extra-bold, black |
| \`Barlow\`              | thin, extra-light, light, regular, medium, semi-bold, bold, extra-bold, black |
| \`Bebas Neue\`          | regular                                                                       |
| \`Bitter\`              | thin, extra-light, light, regular, medium, semi-bold, bold, extra-bold, black |
| \`Bricolage Grotesque\` | extra-light, light, regular, medium, semi-bold, bold, extra-bold              |
| \`Caprasimo\`           | regular                                                                       |
| \`DM Sans\`             | thin, extra-light, light, regular, medium, semi-bold, bold, extra-bold, black |
| \`DM Serif Text\`       | regular                                                                       |
| \`EB Garamond\`         | regular, medium, semi-bold, bold, extra-bold                                  |
| \`Exo 2\`               | thin, extra-light, light, regular, medium, semi-bold, bold, extra-bold, black |
| \`Inter\`               | thin, extra-light, light, regular, medium, semi-bold, bold, extra-bold, black |
| \`Lato\`                | thin, light, regular, bold, black                                             |
| \`Lexend\`              | thin, extra-light, light, regular, medium, semi-bold, bold, extra-bold, black |
| \`Libre Baskerville\`   | regular, medium, semi-bold, bold                                              |
| \`Lora\`                | regular, medium, semi-bold, bold                                              |
| \`Manrope\`             | extra-light, light, regular, medium, semi-bold, bold, extra-bold              |
| \`Merriweather\`        | light, regular, medium, semi-bold, bold, extra-bold, black                    |
| \`Montserrat\`          | thin, extra-light, light, regular, medium, semi-bold, bold, extra-bold, black |
| \`Noto Sans\`           | thin, extra-light, light, regular, medium, semi-bold, bold, extra-bold, black |
| \`Noto Serif\`          | thin, extra-light, light, regular, medium, semi-bold, bold, extra-bold, black |
| \`Nunito Sans\`         | extra-light, light, regular, medium, semi-bold, bold, extra-bold, black       |
| \`Open Sans\`           | light, regular, medium, semi-bold, bold, extra-bold                           |
| \`Oswald\`              | extra-light, light, regular, medium, semi-bold, bold                          |
| \`Outfit\`              | thin, extra-light, light, regular, medium, semi-bold, bold, extra-bold, black |
| \`PT Sans\`             | regular, bold                                                                 |
| \`Playfair Display\`    | regular, medium, semi-bold, bold, extra-bold, black                           |
| \`Poppins\`             | thin, extra-light, light, regular, medium, semi-bold, bold, extra-bold, black |
| \`Quicksand\`           | light, regular, medium, semi-bold, bold                                       |
| \`Raleway\`             | thin, extra-light, light, regular, medium, semi-bold, bold, extra-bold, black |
| \`Roboto\`              | thin, extra-light, light, regular, medium, semi-bold, bold, extra-bold, black |
| \`Space Grotesk\`       | light, regular, medium, semi-bold, bold                                       |
| \`Titillium Web\`       | extra-light, light, regular, semi-bold, bold, black                           |
| \`Ubuntu\`              | light, regular, medium, bold                                                  |
| \`Work Sans\`           | thin, extra-light, light, regular, medium, semi-bold, bold, extra-bold, black |
`;

export const PLACEHOLD_CO_REFERENCE = `## Placehold.co API

Use only as a last resort when no real images are available.

- Size: \`https://placehold.co/600x400\` (10x10 to 4000x4000)
- Format: \`/600x400/png\` (SVG, PNG, JPEG, GIF, WebP, AVIF)
- Colors: \`/600x400/000000/FFF\` (hex or CSS names, both required)
- Text: \`?text=Hello+World\` (only ASCII characters, limit to 3 words)
- Fonts: \`?font=roboto\` (Lato, Lora, Montserrat, Open Sans, Oswald, Playfair Display, Poppins, PT Sans, Raleway, Roboto)
`;

export const BOOLEAN_DSL = `## Boolean Values

| Value                  | Meaning |
| ---------------------- | ------- |
| \`true\`, \`1\`, \`yes\`   | True    |
| \`false\`, \`0\`, \`no\`   | False   |`;

export const BACKGROUND_POSITION_DSL = `## Background Position Values

| Value           | Description   |
| --------------- | ------------- |
| \`left top\`      | Top left      |
| \`center top\`    | Top center    |
| \`right top\`     | Top right     |
| \`left center\`   | Center left   |
| \`center center\` | Center        |
| \`right center\`  | Center right  |
| \`left bottom\`   | Bottom left   |
| \`center bottom\` | Bottom center |
| \`right bottom\`  | Bottom right  |`;

export const ANIMATION_DSL = `## Animation Attributes

Scroll-triggered entry animations, hover effects, and continuous animations bring the page to life. Use them consistently across the page for a cohesive feel.

### Entry Animations (scroll-triggered)

Available on \`<section>\`, \`<row>\`, \`<column>\`, \`<image>\`, and \`<button>\` elements.

| Attribute           | Type    | Default    | Description                                 |
| ------------------- | ------- | ---------- | ------------------------------------------- |
| \`animationType\`     | string  | \`none\`     | Entry animation type (see table below)      |
| \`animationDuration\` | integer | \`600\`      | Duration in milliseconds (400-1200)         |
| \`animationDelay\`    | integer | \`0\`        | Delay in milliseconds before animation starts |
| \`animationEasing\`   | string  | \`ease-out\` | Easing: \`ease-out\`, \`ease-in\`, \`ease-in-out\`, \`linear\` |

#### Animation Types

| Type          | Description                                      | Best For                        |
| ------------- | ------------------------------------------------ | ------------------------------- |
| \`fadeIn\`      | Fades from transparent to opaque                 | Universal, subtle               |
| \`slideUp\`     | Slides up from below while fading in             | Sections, cards, text blocks    |
| \`slideDown\`   | Slides down from above while fading in           | Headers, top elements           |
| \`slideLeft\`   | Slides in from the right                         | Right-column content            |
| \`slideRight\`  | Slides in from the left                          | Left-column content             |
| \`zoomIn\`      | Scales up from smaller while fading in           | Images, cards, icons            |
| \`blurIn\`      | Blurs in from unfocused to clear                 | Hero text, dramatic reveals     |
| \`revealUp\`    | Clip-mask reveal from bottom to top              | Modern, editorial style         |
| \`revealLeft\`  | Clip-mask reveal from right to left              | Bold, directional reveals       |
| \`revealRight\` | Clip-mask reveal from left to right              | Bold, directional reveals       |

### Stagger Delay (containers only)

| Attribute      | Type    | Default | Description                                  |
| -------------- | ------- | ------- | -------------------------------------------- |
| \`staggerDelay\` | integer | \`100\`   | Delay between each child's animation (ms)    |

Use \`staggerDelay\` on a parent container (\`<section>\`, \`<row>\`, \`<column>\`) to create a cascading animation effect where children animate one after another. The children still need their own \`animationType\` set.

### Hover Effects

Available on \`<section>\`, \`<row>\`, \`<column>\`, \`<image>\`, and \`<button>\` elements.

| Attribute                 | Type    | Default | Description                               |
| ------------------------- | ------- | ------- | ----------------------------------------- |
| \`hoverEffect\`             | string  | \`none\`  | Hover effect type (see table below)       |
| \`hoverTransitionDuration\` | integer | \`300\`   | Transition duration in milliseconds       |
| \`hoverIntensity\`          | integer | varies  | Intensity of the hover effect             |

#### Hover Effect Types

| Type       | Description                 | Default Intensity | Best For                     |
| ---------- | --------------------------- | ----------------- | ---------------------------- |
| \`scale\`    | Scales up on hover          | \`105\` (%)         | Cards, images, buttons       |
| \`lift\`     | Lifts up with shadow        | \`4\` (px)          | Cards, testimonial boxes     |
| \`glow\`     | Adds a glow/shadow effect   | \`20\` (px)         | Feature cards, CTAs          |
| \`brighten\` | Brightens the element       | \`110\` (%)         | Images, background elements  |

### Continuous Animations (images only)

Available only on \`<image>\` elements. Creates ongoing motion effects.

| Attribute             | Type    | Default | Description                              |
| --------------------- | ------- | ------- | ---------------------------------------- |
| \`continuousAnimation\` | string  | \`none\`  | Continuous animation type                |
| \`continuousSpeed\`     | integer | varies  | Speed of the animation (seconds)         |
| \`continuousIntensity\` | integer | varies  | Intensity of the motion                  |

#### Continuous Animation Types

| Type        | Description                          | Default Speed | Default Intensity | Best For                      |
| ----------- | ------------------------------------ | ------------- | ----------------- | ----------------------------- |
| \`kenBurns\`  | Slow pan and zoom (documentary feel) | \`15\`          | \`10\`              | Hero backgrounds, galleries   |
| \`slowZoom\`  | Gradual zoom in                      | \`12\`          | \`8\`               | Product images, hero images   |
| \`pulse\`     | Subtle breathing/pulsing             | \`3\`           | \`15\`              | Accent images, call attention |

### Animation Examples

Section with entry animation and staggered children:
\`\`\`xml
<section animationType="fadeIn" animationDuration="800" staggerDelay="150">
  <column animationType="slideUp" animationDuration="600">...</column>
  <column animationType="slideUp" animationDuration="600">...</column>
  <column animationType="slideUp" animationDuration="600">...</column>
</section>
\`\`\`

Card with hover lift effect:
\`\`\`xml
<column bg="rgba(255,255,255,0.1)" borderRadius="12,12,12,12" hoverEffect="lift" hoverIntensity="6">
  <image src="..." hoverEffect="scale" hoverIntensity="103" />
  <text variant="heading.h3"><paragraph>Feature Title</paragraph></text>
</column>
\`\`\`

Hero image with continuous Ken Burns animation:
\`\`\`xml
<image
  src="https://example.com/hero.jpg"
  fit="cover"
  width="100%"
  height="500px"
  continuousAnimation="kenBurns"
  continuousSpeed="15"
  continuousIntensity="10"
/>
\`\`\`
`;

export const STRUCTURE_RULES_DSL = `## Structure Rules

1. Root must be \`<page>\` or \`<section>\`
2. \`<page>\` can only contain \`<section>\` elements
3. \`<section>\` can only be used at the first level inside a page (if not root)
4. Containers (\`<section>\`, \`<row>\`, \`<column>\`) can contain other containers or content elements
5. Content elements (\`<text>\`, \`<image>\`, \`<button>\`, \`<video>\`, \`<icon>\`) cannot have children (except \`<text>\` which requires \`<paragraph>\` tags)
6. \`<form>\` can only contain form field elements
7. \`<dropdown>\` and \`<select>\` require \`<option>\` children`;

/**
 * Get the full DSL reference for layout-only prompts (containers + responsive)
 */
export const getLayoutDslReference = (): string => {
  return [PAGE_DSL, CONTAINER_DSL, RESPONSIVE_DSL, COLOR_DSL, SPACING_DSL].join('\n\n');
};

/**
 * Get the full DSL reference for content generation (all elements)
 */
export const getContentDslReference = (): string => {
  return [
    CONTAINER_DSL,
    RESPONSIVE_DSL,
    TEXT_DSL,
    BUTTON_DSL,
    IMAGE_DSL,
    VIDEO_DSL,
    COUNTDOWN_DSL,
    ICON_DSL,
    FORM_DSL,
    ANIMATION_DSL,
    COLOR_DSL,
    SPACING_DSL,
    FONT_FAMILY_DSL,
    PLACEHOLD_CO_REFERENCE,
  ].join('\n\n');
};

/**
 * Get DSL reference for a specific section type
 */
export const getSectionTypeDslReference = (sectionType: string): string => {
  const base = [
    CONTAINER_DSL,
    RESPONSIVE_DSL,
    TEXT_DSL,
    BUTTON_DSL,
    IMAGE_DSL,
    ICON_DSL,
    COLOR_DSL,
    SPACING_DSL,
  ];
  const lowerType = sectionType.toLowerCase();

  if (
    lowerType.includes('form') ||
    lowerType.includes('contact') ||
    lowerType.includes('cta') ||
    lowerType.includes('newsletter') ||
    lowerType.includes('hero')
  ) {
    base.push(FORM_DSL);
  }
  if (lowerType.includes('video')) {
    base.push(VIDEO_DSL);
  }
  if (
    lowerType.includes('countdown') ||
    lowerType.includes('timer') ||
    lowerType.includes('urgency') ||
    lowerType.includes('cta')
  ) {
    base.push(COUNTDOWN_DSL);
  }

  base.push(ANIMATION_DSL);
  base.push(PLACEHOLD_CO_REFERENCE);

  return base.join('\n\n');
};

/**
 * Get the complete DSL reference for full page generation prompts.
 * Used by pageGenerationSystemPrompt for the monolithic AI generation flow.
 */
export const getFullDslReference = (): string => {
  return `# XML DSL Reference

A simplified XML syntax that converts to CraftJS JSON for the page builder.

## Types Reference

${COLOR_DSL}

${SPACING_DSL}

${BOOLEAN_DSL}

${BACKGROUND_POSITION_DSL}

${RESPONSIVE_DSL}

## Root Elements

${PAGE_DSL}

${CONTAINER_DSL}

## Content Elements

${TEXT_DSL}

${BUTTON_DSL}

${IMAGE_DSL}

${VIDEO_DSL}

${COUNTDOWN_DSL}

${ICON_DSL}

${FORM_DSL}

${ANIMATION_DSL}

${FONT_FAMILY_DSL}

${STRUCTURE_RULES_DSL}`;
};
